import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, imgSrc } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { MapPin, Star, Users, Check } from "lucide-react";
import { format } from "date-fns";

const HOURS = Array.from({ length: 24 }, (_, i) => `${String(i).padStart(2, "0")}:00`);

export default function SpaceDetail() {
  const { id } = useParams();
  const [listing, setListing] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [date, setDate] = useState(new Date());
  const [start, setStart] = useState("18:00");
  const [end, setEnd] = useState("21:00");
  const [notes, setNotes] = useState("");
  const [booking, setBooking] = useState(false);
  const [booked, setBooked] = useState([]);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get(`/listings/${id}`).then((r) => setListing(r.data)).catch(() => toast.error("Listing not found"));
    api.get(`/reviews/listing/${id}`).then((r) => setReviews(r.data)).catch(() => {});
  }, [id]);

  useEffect(() => {
    if (!id) return;
    api
      .get(`/listings/${id}/availability`, { params: { date: format(date, "yyyy-MM-dd") } })
      .then((r) => setBooked(r.data))
      .catch(() => setBooked([]));
  }, [id, date]);

  if (!listing) return <div className="p-20 text-center text-neutral-500">Loading…</div>;

  const hours = Math.max(0, (parseInt(end) * 60 - parseInt(start) * 60) / 60 + (parseInt(end.split(":")[1]) - parseInt(start.split(":")[1])) / 60);
  const subtotal = hours * listing.price_per_hour;
  const fee = subtotal * 0.15;
  const deposit = listing.deposit || 0;
  const total = subtotal + fee + deposit;

  const toMin = (t) => parseInt(t.split(":")[0]) * 60 + parseInt(t.split(":")[1]);
  const conflict = booked.some((b) => toMin(start) < toMin(b.end_time) && toMin(end) > toMin(b.start_time));

  const book = async () => {
    if (!user) { navigate("/login"); return; }
    if (hours <= 0) { toast.error("Pick a valid time range"); return; }
    if (conflict) { toast.error("That time overlaps an existing booking"); return; }
    setBooking(true);
    try {
      const { data: bk } = await api.post("/bookings", {
        listing_id: id,
        date: format(date, "yyyy-MM-dd"),
        start_time: start,
        end_time: end,
        notes,
      });
      const { data: co } = await api.post("/payments/checkout", {
        booking_id: bk.id,
        origin_url: window.location.origin,
      });
      window.location.href = co.checkout_url;
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 403 && detail === "IDENTITY_REQUIRED") {
        toast.message("Quick ID check needed before your first booking");
        navigate("/verify-identity");
        return;
      }
      toast.error(detail || "Booking failed");
      setBooking(false);
    }
  };

  const photos = listing.photos?.length ? listing.photos : [];

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
      {/* Header */}
      <div className="mb-6">
        <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-neutral-500 mb-2">{listing.category}</div>
        <h1 className="font-display font-black tracking-tighter text-4xl md:text-5xl leading-[1.02]">{listing.title}</h1>
        <div className="mt-3 flex items-center gap-4 text-sm text-neutral-600 flex-wrap">
          <span className="flex items-center gap-1"><MapPin className="w-4 h-4" />{listing.city} · {listing.address}</span>
          <span className="flex items-center gap-1"><Users className="w-4 h-4" />Up to {listing.capacity}</span>
          {listing.reviews_count > 0 && (
            <span className="flex items-center gap-1"><Star className="w-4 h-4 fill-[#FF4500] text-[#FF4500]" />{listing.rating.toFixed(1)} ({listing.reviews_count})</span>
          )}
        </div>
      </div>

      {/* Bento photo grid */}
      <div className="grid grid-cols-4 grid-rows-2 gap-2 rounded-xl overflow-hidden aspect-[16/8] mb-12" data-testid="photo-grid">
        <div className="col-span-2 row-span-2 bg-neutral-100">
          {photos[0] && <img src={imgSrc(photos[0])} alt="" className="w-full h-full object-cover" />}
        </div>
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="bg-neutral-100">
            {photos[i] && <img src={imgSrc(photos[i])} alt="" className="w-full h-full object-cover" />}
          </div>
        ))}
      </div>

      {/* Two-col */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-12">
        <div className="space-y-10">
          <section>
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-neutral-500 mb-3">HOSTED BY</div>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-neutral-900 text-white grid place-items-center font-bold">
                {listing.host_name?.[0]?.toUpperCase()}
              </div>
              <div>
                <div className="font-display font-bold text-lg">{listing.host_name}</div>
                <div className="text-xs text-neutral-500">SpareHours host</div>
              </div>
            </div>
          </section>

          <section>
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-neutral-500 mb-3">ABOUT THIS SPACE</div>
            <p className="text-neutral-800 leading-relaxed">{listing.description}</p>
          </section>

          {listing.amenities?.length > 0 && (
            <section>
              <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-neutral-500 mb-3">WHAT'S INCLUDED</div>
              <div className="grid grid-cols-2 gap-3">
                {listing.amenities.map((a) => (
                  <div key={a} className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4 text-[#FF4500]" /> {a}
                  </div>
                ))}
              </div>
            </section>
          )}

          {listing.rules && (
            <section>
              <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-neutral-500 mb-3">HOUSE RULES</div>
              <p className="text-neutral-700 leading-relaxed">{listing.rules}</p>
            </section>
          )}

          <section>
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-neutral-500 mb-3">
              REVIEWS ({reviews.length})
            </div>
            {reviews.length === 0 ? (
              <div className="text-sm text-neutral-500">No reviews yet.</div>
            ) : (
              <div className="space-y-4">
                {reviews.map((r) => (
                  <div key={r.id} className="border-t border-neutral-200 pt-4">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="font-bold text-sm">{r.renter_name}</div>
                      <div className="flex items-center gap-0.5 text-[#FF4500]">
                        {Array.from({ length: r.rating }).map((_, i) => <Star key={i} className="w-3 h-3 fill-current" />)}
                      </div>
                    </div>
                    <div className="text-sm text-neutral-700">{r.comment}</div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>

        {/* Booking widget */}
        <aside className="lg:sticky lg:top-24 h-max border border-neutral-200 rounded-xl bg-white p-6" data-testid="booking-widget">
          <div className="flex items-baseline gap-1 mb-4">
            <span className="font-display font-black text-3xl">${listing.price_per_hour}</span>
            <span className="text-neutral-500 text-sm">/ hour</span>
          </div>

          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-neutral-500 mb-2">DATE</div>
          <div className="border border-neutral-200 rounded-lg p-2 mb-4">
            <Calendar
              mode="single"
              selected={date}
              onSelect={(d) => d && setDate(d)}
              disabled={{ before: new Date() }}
              data-testid="booking-calendar"
            />
          </div>

          <div className="grid grid-cols-2 gap-3 mb-4">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-neutral-500 mb-1">Start</div>
              <Select value={start} onValueChange={setStart}>
                <SelectTrigger data-testid="booking-start-select"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-60">
                  {HOURS.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-neutral-500 mb-1">End</div>
              <Select value={end} onValueChange={setEnd}>
                <SelectTrigger data-testid="booking-end-select"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-60">
                  {HOURS.map((h) => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <Textarea
            placeholder="Notes for the host (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="mb-4"
            data-testid="booking-notes"
          />

          {/* Breakdown */}
          <div className="border-t border-neutral-200 pt-4 space-y-2 text-sm">
            <div className="flex justify-between text-neutral-600">
              <span>${listing.price_per_hour} × {hours.toFixed(1)} h</span>
              <span>${subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-neutral-600">
              <span>Service fee (15%)</span>
              <span>${fee.toFixed(2)}</span>
            </div>
            {deposit > 0 && (
              <div className="flex justify-between text-neutral-600" data-testid="booking-deposit-row">
                <span>Refundable deposit</span>
                <span>${deposit.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between font-display font-bold text-base pt-2 border-t border-neutral-200">
              <span>Total{deposit > 0 ? " due" : ""}</span>
              <span data-testid="booking-total">${total.toFixed(2)}</span>
            </div>
            {deposit > 0 && (
              <p className="text-[11px] text-neutral-500 pt-1">
                Includes a ${deposit.toFixed(2)} refundable deposit, returned after a clean booking.
              </p>
            )}
          </div>

          {booked.length > 0 && (
            <div className="mb-4 rounded-lg bg-neutral-50 border border-neutral-200 p-3" data-testid="booked-slots">
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-neutral-500 mb-1">Already booked this day</div>
              <div className="flex flex-wrap gap-1.5">
                {booked.map((b, i) => (
                  <span key={i} className="text-xs font-mono px-2 py-0.5 rounded-full bg-neutral-200 text-neutral-700">
                    {b.start_time}–{b.end_time}
                  </span>
                ))}
              </div>
            </div>
          )}

          {conflict && (
            <div className="mb-3 text-xs text-red-600 font-medium" data-testid="conflict-warning">
              This time overlaps an existing booking. Pick another slot.
            </div>
          )}

          <Button
            onClick={book}
            disabled={booking || hours <= 0 || conflict}
            className="w-full mt-2 bg-[#FF4500] hover:bg-[#E63E00] text-white h-12 rounded-full font-medium"
            data-testid="booking-submit-btn"
          >
            {booking ? "Redirecting to payment…" : user ? "Book & pay" : "Log in to book"}
          </Button>
          <p className="text-[11px] text-neutral-500 text-center mt-3">
            Secure checkout via Stripe. Your slot is confirmed once payment clears.
          </p>
          <p className="text-[11px] text-neutral-400 text-center mt-2">
            By booking you agree to our{" "}
            <Link to="/terms" className="underline hover:text-[#FF4500]">Terms</Link>,{" "}
            <Link to="/cancellation" className="underline hover:text-[#FF4500]">Cancellation Policy</Link> and{" "}
            <Link to="/privacy" className="underline hover:text-[#FF4500]">Privacy Policy</Link>.
          </p>
        </aside>
      </div>
    </div>
  );
}
